import { useEffect, useState } from "react";
import { Bell, BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported, isIOS, isStandalone, getPushSubscription, subscribeToPush, unsubscribeFromPush,
} from "@/lib/push";
import { isNativeApp } from "@/lib/nativeAudio";

/** Header bell — opt in/out of "estamos ao vivo" push alerts. */
const NotificationBell = () => {
  const [state, setState] = useState("loading"); // loading | off | on | unsupported
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isNativeApp() || !pushSupported()) {
      setState("unsupported");
      return;
    }
    getPushSubscription()
      .then((sub) => setState(sub && Notification.permission === "granted" ? "on" : "off"))
      .catch(() => setState("off"));
  }, []);

  const onClick = async () => {
    if (state === "unsupported") {
      if (isIOS() && !isStandalone()) {
        toast("Instale o app para receber avisos", {
          description: "No iPhone: toque em Compartilhar → “Adicionar à Tela de Início”. Depois ative o sininho.",
          duration: 8000,
        });
      } else {
        toast.error("Seu navegador não suporta notificações.");
      }
      return;
    }
    setBusy(true);
    try {
      if (state === "on") {
        await unsubscribeFromPush();
        setState("off");
        toast("Avisos desativados", { description: "Você não receberá mais alertas de transmissão." });
      } else {
        await subscribeToPush();
        setState("on");
        toast.success("Avisos ativados!", {
          description: "Você será avisado assim que a Fala Itapira entrar ao vivo.",
        });
      }
    } catch (e) {
      if (e?.message === "permission-denied" || Notification.permission === "denied") {
        toast.error("Permissão bloqueada", {
          description: "Libere as notificações para este site nas configurações do navegador.",
        });
      } else {
        toast.error("Não foi possível ativar os avisos agora.");
      }
    } finally {
      setBusy(false);
    }
  };

  const on = state === "on";
  if (isNativeApp()) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || state === "loading"}
      data-testid="notification-bell"
      aria-label={on ? "Desativar avisos de transmissão" : "Ativar avisos de transmissão"}
      aria-pressed={on}
      title={on ? "Avisos ativados" : "Avise-me quando estiver ao vivo"}
      className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-colors disabled:opacity-60 ${
        on ? "bg-[var(--fi-red)] text-white hover:bg-[var(--fi-red-hover)]" : "bg-white/10 text-white hover:bg-white/20"
      }`}
    >
      {busy || state === "loading" ? (
        <Loader2 className="w-[18px] h-[18px] animate-spin" />
      ) : on ? (
        <BellRing className="w-[18px] h-[18px]" />
      ) : (
        <Bell className="w-[18px] h-[18px]" />
      )}
      {!on && state === "off" && (
        <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[var(--fi-red)] live-dot" />
      )}
    </button>
  );
};

export default NotificationBell;
